/*
  The cert-lookup hero (§3.4, §6.1, D19, D22), rebuilt in Tailwind.

  **The band no longer has a fixed-dark half, and D22 is therefore no longer a boundary.**
  It used to be: a photo backdrop with overlay text on it, FIXED DARK in both themes
  (§3.4, D14), against four panels that followed the page theme. The photo is gone
  (docs/specs/hero-stats-band.md, H1) and the band is now an ordinary themed surface —
  `bg-pcg-page` / `text-pcg-text` — so everything here themes, panels included. What
  survives of D22 is the reason the panels keep their own fill: `PANEL` below is
  `bg-pcg-card` plus `skin-surface--overlay`, which is what stops the search box from
  dissolving into the band behind it.

  `text-white` is still wrong here, but for the ordinary reason rather than a special
  one: Tailwind's `white` is pointed at `--pcg-fg` (tailwind.config.js), so it flips to
  near-black on paper. Use the text tokens.

  Two MUI behaviours were replaced with real ones rather than dropped:

  - `ClickAwayListener` -> a pointerdown/focusin listener scoped to the hero panel, which
    also closes on Escape. The MUI version wrapped only the results panel, so clicking
    BACK INTO the search box counted as clicking away and wiped the results the visitor
    was about to page through. The dismiss region is now the whole panel column, so the
    overlay closes when the visitor leaves the hero and not before.

  - `Collapse` -> a `grid-template-rows: 0fr -> 1fr` transition, with `invisible` as the
    closed visual state. Keeping the closed fields out of the tab order is `inert`'s job,
    not `visibility`'s — the note on the effect below says why that distinction is worth
    a paragraph.
*/

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import debounce from 'lodash/debounce';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';

import { usePrefetch } from 'services/pcgPublic';
import { useSearchGradingsQuery } from 'services/pcgPublicV2';

import { normaliseCertNumber, usablePartName } from 'lib';

import { CONTROL_BASE, CONTROL_LABEL, FOCUS_RING, PAGE_CONTAINER } from 'components/styles';

import { CRUMBS_SLOT_ID } from 'components/Breadcrumbs';
import SearchCardsRowTable from 'components/Tables/SearchCardsRowTable';
import SearchCardsAdvanced from 'components/SearchCardsAdvanced';

/* The server `limit`, and therefore the table's fixed page size (D19). */
const ROWS_PER_PAGE = 10;

/*
  The panels inside the hero theme with the page (D22) — this is their shared chrome.

  `skin-surface--overlay` is the gilded exception to "panels lose their borders": these
  four hang OVER the photo and over the page below it, so a borderless transparent band
  would read as text floating on the hero. Gilded keeps their fill and their edge and
  only flattens the radius and drops the shadow. Nothing in navy selects the class.
*/
const PANEL =
  'skin-surface--overlay border border-pcg-border bg-pcg-card text-pcg-text shadow-lg';

const ICON_BUTTON =
  `inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-pcg-textMuted hover:text-pcg-text ${FOCUS_RING}`;

/*
  The home page's lookup is three labelled fields, not the one box (D24-D25 of
  docs/specs/pop-language-split.md, Robert, 2026-10-20).

  On home the lookup is the left column beside the Population Report column, and that
  column is a label over a full-width search, then two labelled controls side by side.
  The single box beside it left the left column a heading and one line, with dead space
  under it the height of the controls opposite. So home now asks the same way the column
  next to it does: Cert Number across the top, Card Name and Set Name sharing the line
  under it, each with its own label and an example in the placeholder. The classes are
  FilterSearch's and Select's (CONTROL_BASE, CONTROL_LABEL), so the two columns read as
  one form rather than two designs that happen to be adjacent.

  The filled fields INTERSECT (`useOr: false`, D25) — a visitor who types a card name and
  a set name means that card in that set, not either. That is the advanced panel's
  semantics, which is also why home has no advanced chevron: these three are the advanced
  fields people actually use. Inner pages keep the one box and the chevron (D15).

  Set Name matches PART of a set name (`setNameLike`, R13 of
  docs/specs/pop-report-followups.md), as card names already do. The advanced panel's Set
  Name select still sends the exact `setName`, because it picks from full names.
*/
type HomeFields = { gradedId: string; cardName: string; setNameLike: string };

const NO_HOME_FIELDS: HomeFields = { gradedId: '', cardName: '', setNameLike: '' };

/*
  What is sent, as against what is in the boxes: the cert number padded to nine digits
  (D26, lib/certNumber.ts), the names trimmed so a stray space is not a search of its own.
  A set name below D13's minimum (one Latin letter, say) goes out as '' — not sent, and
  not a search on its own — since the backend would drop it anyway.
*/
const toHomeSearch = (fields: HomeFields): HomeFields => ({
  gradedId: normaliseCertNumber(fields.gradedId),
  cardName: fields.cardName.trim(),
  setNameLike: usablePartName(fields.setNameLike) ?? '',
});

type HomeFieldProps = {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  /* The field focuses itself again after clearing; the caller only resets the value. */
  onClear: () => void;
  onFocus: (input: HTMLInputElement) => void;
  inputMode?: 'numeric';
  /* The search glyph, as on the Population Report column's full-width search. */
  withIcon?: boolean;
  className?: string;
  /* Anything that hangs from the input — the card-name field's suggestions. */
  children?: ReactNode;
};

/*
  One labelled home field. FilterSearch's markup, but controlled: the lookup has to read
  all three values at once to send them as one query, and to put them back after the
  results are dismissed, which FilterSearch's own internal state cannot do.
*/
const HomeField = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  onClear,
  onFocus,
  inputMode,
  withIcon = false,
  className = '',
  children,
}: HomeFieldProps) => {
  const fieldRef = useRef<HTMLInputElement>(null);

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label htmlFor={id} className={CONTROL_LABEL}>
        {label}
      </label>
      <div className="relative">
        {withIcon ? (
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-pcg-textMuted"
          />
        ) : null}
        <input
          id={id}
          name={id}
          ref={fieldRef}
          type="text"
          inputMode={inputMode}
          autoComplete="off"
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onFocus={(event) => onFocus(event.currentTarget)}
          className={`w-full py-2 pr-10 ${withIcon ? 'pl-9' : 'pl-3'} ${CONTROL_BASE} placeholder:text-pcg-textDim ${FOCUS_RING}`}
        />
        {value ? (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            onClick={() => {
              onClear();
              fieldRef.current?.focus();
            }}
            className={`absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-pcg-textMuted hover:text-pcg-text ${FOCUS_RING}`}
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        ) : null}
        {children}
      </div>
    </div>
  );
};

type SearchCardsRowProps = {
  /*
    True on the home page, where the lookup is the left column of a row in HomeView, beside
    the population search and under the banner image and Population Report card (Robert,
    2026-10-20). HomeView owns the width, the padding and the background there, so the
    section drops its own container, its 25vh floor and its padding. Every other page keeps
    the full-width band exactly as it was.
  */
  belowBanner?: boolean;
};

const SearchCardsRow = ({ belowBanner = false }: SearchCardsRowProps) => {
  const [value, setValue] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const [advSearchValue, setAdvSearchValue] = useState({});
  const [showAdvSearch, setShowAdvSearch] = useState(false);
  /*
    The advanced fields mount the first time the panel opens, then stay mounted so a
    visitor who closes and reopens it keeps what they typed. Mounting them up front cost
    every page a 295 KB /v1/gameInfo?includeCounts=true for dropdowns almost nobody opens,
    in flight alongside the requests the page actually needed. Hovering or focusing the
    toggle starts that fetch early, so the dropdowns are usually filled by the time the
    panel opens.
  */
  const [advancedMounted, setAdvancedMounted] = useState(false);
  const prefetchGameInfo = usePrefetch('getGameInfo');
  const prefetchGradeNames = usePrefetch('getGradeNames');
  const prefetchAdvanced = () => {
    prefetchGameInfo();
    prefetchGradeNames();
  };
  const [showAutocomplete, setShowAutocomplete] = useState(true);

  /* Home only (D24): what is in the three boxes, and what was last sent from them. */
  const [homeFields, setHomeFields] = useState<HomeFields>(NO_HOME_FIELDS);
  const [homeSearch, setHomeSearch] = useState<HomeFields>(NO_HOME_FIELDS);

  const [page, setPage] = useState(0);

  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /* Home's Escape target: whichever of the three fields the visitor was last in. */
  const lastHomeFieldRef = useRef<HTMLInputElement | null>(null);
  const advancedRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Build search params based on the home fields, or the basic or advanced search mode.
  // Home and advanced mode intersect their fields (useOr=false, D25); the basic box keeps
  // the default union so one string can match either a card name or a cert number. Only
  // the cert-number reading of that string is padded (D26) — normaliseCertNumber leaves
  // anything that is not a cert number as it was, so a card name still goes out as typed.
  const searchParams = belowBanner
    ? { ...homeSearch, useOr: false }
    : showAdvSearch
      ? { ...advSearchValue, useOr: false }
      : { cardName: searchValue, gradedId: normaliseCertNumber(searchValue) };
  const hasSearch = belowBanner
    ? Object.values(homeSearch).some(v => v !== '')
    : showAdvSearch
      ? Object.values(advSearchValue).some(v => v !== '')
      : !!searchValue;

  // offset/limit are honoured by the endpoint for both of those query shapes — verified
  // against live responses, see the note at the top of SearchCardsRowTable.
  const { data, error, isFetching } = useSearchGradingsQuery(
    {
      ...searchParams,
      offset: page * ROWS_PER_PAGE,
      limit: ROWS_PER_PAGE,
    },
    { skip: !hasSearch }
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebounce = useCallback(
    debounce((_searchVal: string) => {
      setPage(0);
      setSearchValue(_searchVal);
    }, 300),
    []
  );

  const handleOnChange = (value: string) => {
    setValue(value);
    handleDebounce(value);
    setShowAutocomplete(true);
  }

  /*
    Home's debounce takes all three fields at once, so typing in one and then another
    inside 300ms sends one query with both, not a query per field.
  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleHomeDebounce = useCallback(
    debounce((fields: HomeFields) => {
      setPage(0);
      setHomeSearch(toHomeSearch(fields));
    }, 300),
    []
  );

  const handleHomeChange = (field: keyof HomeFields, next: string) => {
    const fields = { ...homeFields, [field]: next };
    setHomeFields(fields);
    handleHomeDebounce(fields);
    setShowAutocomplete(true);
  };

  /* Immediate, like FilterSearch's: a pending keystroke must not land after the clear. */
  const handleHomeClear = (field: keyof HomeFields) => {
    handleHomeDebounce.cancel();
    const fields = { ...homeFields, [field]: '' };
    setHomeFields(fields);
    setPage(0);
    setHomeSearch(toHomeSearch(fields));
  };

  /*
    Focusing back into a field after the results were dismissed brings them back, as
    focusing the single box does. Only then, though: re-sending on every focus would put
    the table back on page 1 each time the visitor tabs from one field to the next.
  */
  const handleHomeFocus = (input: HTMLInputElement) => {
    lastHomeFieldRef.current = input;
    if (!hasSearch) {
      handleHomeDebounce(homeFields);
    }
  };

  /* Both halves of "the visitor is done with these results". */
  const closeResults = useCallback(() => {
    setShowAutocomplete(false);
    setSearchValue('');
    setHomeSearch(NO_HOME_FIELDS);
  }, []);

  /*
    ClickAwayListener's replacement. mousedown rather than click so the overlay closes on
    the press, as MUI's did; focusin so tabbing out of the hero closes it too, which the
    MUI version never handled. Both listen in the capture phase, so a handler inside the
    panel calling stopPropagation cannot strand the overlay open.
  */
  useEffect(() => {
    if (!hasSearch && !showAutocomplete) {
      return undefined;
    }

    const isOutside = (target: EventTarget | null) =>
      target instanceof Node && !panelRef.current?.contains(target);

    const handlePointerDown = (event: MouseEvent) => {
      if (isOutside(event.target)) {
        closeResults();
      }
    };
    const handleFocusIn = (event: FocusEvent) => {
      if (isOutside(event.target)) {
        closeResults();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeResults();
        /* Escape should leave the visitor where they were, not adrift on <body>. */
        (belowBanner ? lastHomeFieldRef : inputRef).current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasSearch, showAutocomplete, closeResults, belowBanner]);

  /*
    The closed panel's fields have to leave the tab order the MOMENT it starts closing,
    and `invisible` could not do that.

    `visibility` is transitioned by `transition-all`, and it interpolates discretely in
    the visible direction: on close it stays `visible` for the whole 200ms and flips only
    at the end. So for those 200ms the eleven fields and two buttons were still tabbable
    while the container already carried `aria-hidden="true"` — axe's `aria-hidden-focus`,
    and not a theoretical one: two Tabs from the toggle landed on `#gradedId` inside a
    subtree screen readers had just been told to ignore. It predates the gilded theme and
    reproduces under either skin, since neither touches this transition.

    `inert` is the primitive that actually means "not here": it takes the subtree out of
    BOTH the tab order and the accessibility tree, and it is an attribute rather than a
    style, so nothing transitions it. That also makes `aria-hidden` redundant, which is
    why the attribute is gone rather than merely retimed — a container holding focusable
    children is the one place `aria-hidden` must never be used. `invisible` stays on as
    the visual state and as the fallback for anything too old for `inert`.

    Set through the DOM because React 17 has no `inert` prop and TypeScript 4.6's lib.dom
    has no `inert` property; the attribute is what drives the behaviour in any case.
    Layout effect, not effect, so it lands before the closing frame is painted.
  */
  useLayoutEffect(() => {
    const advanced = advancedRef.current;
    if (!advanced) {
      return;
    }

    if (showAdvSearch) {
      advanced.removeAttribute('inert');
      return;
    }

    /* Inerting the subtree focus is sitting in would strand it on <body>. */
    if (advanced.contains(document.activeElement)) {
      toggleRef.current?.focus();
    }
    advanced.setAttribute('inert', '');
  }, [showAdvSearch]);

  /*
    Autocomplete suggestions (top 5 grading results). On home they hang from the Card Name
    field, since a name is what a suggestion completes; the cert and set fields have none.
  */
  const autocompleteSuggestions = !showAdvSearch && data?.data ? data.data.slice(0, 5) : [];
  const suggestionSource = belowBanner ? homeFields.cardName : value;
  const showAutocompletePanel =
    showAutocomplete &&
    !showAdvSearch &&
    suggestionSource.length >= 3 &&
    autocompleteSuggestions.length > 0;

  const showResults = hasSearch;

  const handleClear = () => {
    handleDebounce.cancel();
    setPage(0);
    setValue('');
    setSearchValue('');
    inputRef.current?.focus();
  };

  /* Switching modes is a different query, so it starts at the first page (§6.5). */
  const handleToggleAdvanced = () => {
    setPage(0);
    setAdvancedMounted(true);
    setShowAdvSearch(current => !current);
  };

  /* So is changing an advanced field — the old hero left the page index behind. */
  const handleAdvancedSearch = (next: Record<string, string>) => {
    setPage(0);
    setAdvSearchValue(next);
  };

  /*
    PANEL 2 — the autocomplete dropdown. Themed (D22). Built once and placed in either
    layout: under the single box on inner pages, under Card Name on home.

    Plain buttons in a labelled group rather than the old role="option" divs: those had
    no keyboard path at all, and there is no combobox here for an option to belong to.
  */
  const suggestionsPanel = showAutocompletePanel ? (
    <div
      role="group"
      aria-label="Search suggestions"
      className={`absolute left-0 right-0 top-full z-20 max-h-[300px] overflow-y-auto rounded-b-md ${PANEL}`}
    >
      {autocompleteSuggestions.map((result) => (
        <button
          type="button"
          key={result.gradedId}
          onClick={() => setShowAutocomplete(false)}
          className={`flex w-full items-center gap-3 border-b border-pcg-borderSoft px-3 py-2 text-left hover:bg-pcg-raised ${FOCUS_RING}`}
        >
          {/*
            `image`, not `imageUrl`. CardInfo has no imageUrl (types/gameInfo.ts)
            and the live payload has none either, so the old `any`-typed
            suggestion row asked for a field that was always undefined and the
            thumbnail never once rendered.
          */}
          {result.cardInfo?.image && (
            <img
              src={result.cardInfo.image}
              alt=""
              className="h-14 w-10 shrink-0 object-contain"
              loading="lazy"
            />
          )}
          <span className="min-w-0">
            <span className="block truncate font-bold text-pcg-text">
              {result.cardInfo?.cardName || result.gradedId}
            </span>
            <span className="block truncate text-sm text-pcg-textMuted">
              {result.cardInfo?.setName}
              {result.cardInfo?.number ? ` • #${result.cardInfo.number}` : ''}
              {result.gradedId ? ` • ${result.gradedId}` : ''}
            </span>
          </span>
        </button>
      ))}
      <button
        type="button"
        onClick={() => setShowAutocomplete(false)}
        className={`block w-full px-3 py-2 text-center text-sm font-bold text-pcg-goldText hover:bg-pcg-raised ${FOCUS_RING}`}
      >
        See all results
      </button>
    </div>
  ) : null;

  return (
    /*
      THEMED, like the rest of the page. This band WAS fixed dark in both themes (§3.4,
      D14) with a literal grey and pcg-onAccent text, because a photograph sat behind it.
      The photo is gone (hero-stats-band.md, H1) and the literals went with it: tokens
      here now, and the band follows light/dark like any other surface.

      `bg-pcg-page` rather than `bg-pcg-card` on purpose — the panels below are
      `bg-pcg-card`, and putting the band on the same token would leave the search box
      separated from its own ground by nothing but a hairline (H1, §4.3).
    */
    <section
      aria-label="Cert lookup and pop search"
      className={`relative w-full text-pcg-text ${belowBanner ? '' : 'bg-pcg-page'}`}
    >

      <div
        className={`relative flex w-full items-center ${
          belowBanner ? '' : `${PAGE_CONTAINER} justify-start`
        }`}
      >
        {/*
          No `skin-hero` here, and the gilded rule is not on this band.

          The codebase calls this section the cert-lookup hero (§3.4, §6.1), but the
          mockup's `.m-hero` — the thing the plan's "hero centres and gains a gilded
          hairline" describes — is the block holding the page's own h1, eyebrow and
          standfirst. The mockup does not model a photo band at all. Checked in the
          browser first: a gold rule over a photograph brightened x3 is not subtle, it is
          invisible, which is the other reason it does not belong here. It lives on the
          page heading instead (PAGE_HEADING / PageHeadingRule in views/pageHeading.tsx).
        */}
        {/*
          On home the lookup fills its column, level with the population search beside it,
          and spaces its heading from its fields with the same `flex-col gap-4` that column
          uses, so the labels on the two sides line up across the row (D24).

          On every other page it is a slim band (Robert, 2026-10-20: the tops of the inner
          pages felt empty): from `lg` the heading and the search share one row, the search
          on the right, instead of stacking in a 25vh block with the right half bare.

          And the row is centred, not bottom-aligned, because the page's breadcrumb now
          sits under the heading (D15 of docs/specs/pop-language-split.md, Robert,
          2026-10-20: "Breadcrumb replaces it"), so the title block is two lines tall and
          the search box belongs level with the middle of it rather than its last line.
        */}
        <div
          className={`w-full text-left ${
            belowBanner
              ? 'flex flex-col gap-4'
              : 'pb-2 pt-8 lg:grid lg:grid-cols-[auto_minmax(0,720px)] lg:items-center lg:justify-between lg:gap-12'
          }`}
        >
          {/*
            The eyebrow and the rule are the event skin's (src/skins/event.css): `hidden`
            here, so navy and gilded render neither, exactly as before.

            The slot is where the inner pages' <Breadcrumbs> portals itself (D15). It is
            empty, and `empty:hidden`, on home and on any page with no trail, and the event
            skin hides its gold rule only while the slot has something in it — so the trail
            takes the rule's place, and a page without one keeps the rule. The slot must
            stay IMMEDIATELY before the rule: event.css finds it with `+`.

            Off home the heading drops its own bottom margin and the block carries it
            instead, below `lg` only: stacked on a phone it is the gap above the full-width
            search, and from `lg`, where the search is beside the block, a margin under
            the heading would only push the centring off.
          */}
          <div className={belowBanner ? '' : 'mb-4 lg:mb-0'}>
            <p className="skin-eyebrow hidden">Premier Card Grading</p>
            {/* Balanced, so the narrower home column wraps it as two even lines, not one word. */}
            <h2
              className={`skin-hero-heading text-3xl text-pcg-text [text-wrap:balance] ${
                belowBanner ? 'mb-4' : 'mb-0'
              }`}
            >
              Cert Lookup &amp; Pop Search
            </h2>
            {belowBanner ? null : (
              <div id={CRUMBS_SLOT_ID} className="skin-crumbs-slot mt-3 empty:hidden" />
            )}
            <span aria-hidden="true" className="skin-hero-rule hidden" />
          </div>

          {/* The dismiss scope AND the positioning context for both overlays. */}
          <div ref={panelRef} className="relative">
            {belowBanner ? (
              /*
                Home's three fields (D24): Cert Number across the column, Card Name and Set
                Name sharing the line under it from `sm`, stacked on a phone — the grid the
                Population Report column uses for its search and its two selects.
              */
              <div className="grid gap-3 sm:grid-cols-2">
                <HomeField
                  id="home-cert-number"
                  label="Cert Number"
                  placeholder="e.g. 186853"
                  inputMode="numeric"
                  withIcon
                  value={homeFields.gradedId}
                  onChange={(next) => handleHomeChange('gradedId', next)}
                  onClear={() => handleHomeClear('gradedId')}
                  onFocus={handleHomeFocus}
                  className="sm:col-span-2"
                />
                <HomeField
                  id="home-card-name"
                  label="Card Name"
                  placeholder="e.g. Charizard ex"
                  value={homeFields.cardName}
                  onChange={(next) => handleHomeChange('cardName', next)}
                  onClear={() => handleHomeClear('cardName')}
                  onFocus={handleHomeFocus}
                >
                  {suggestionsPanel}
                </HomeField>
                <HomeField
                  id="home-set-name"
                  label="Set Name"
                  /* Part of the name (setNameLike, R13), so "Blaze" finds Double Blaze. */
                  placeholder="Part of the name, e.g. Blaze"
                  value={homeFields.setNameLike}
                  onChange={(next) => handleHomeChange('setNameLike', next)}
                  onClear={() => handleHomeClear('setNameLike')}
                  onFocus={handleHomeFocus}
                />
              </div>
            ) : (
              <>
                {/* PANEL 1 — the search input. Themed (D22). */}
                <div
                  className={`relative flex items-center ${PANEL} ${
                    showResults || showAdvSearch ? 'rounded-t-md' : 'rounded-md'
                  }`}
                >
                  <button
                    type="button"
                    ref={toggleRef}
                    onClick={handleToggleAdvanced}
                    onMouseEnter={prefetchAdvanced}
                    onFocus={prefetchAdvanced}
                    aria-expanded={showAdvSearch}
                    aria-controls="pcg-advanced-search"
                    aria-label={showAdvSearch ? 'Hide advanced search' : 'Show advanced search'}
                    className={ICON_BUTTON}
                  >
                    {showAdvSearch ? (
                      <ChevronUp aria-hidden="true" className="h-5 w-5" />
                    ) : (
                      <ChevronDown aria-hidden="true" className="h-5 w-5" />
                    )}
                  </button>
                  <span aria-hidden="true" className="mx-1 h-7 w-px bg-pcg-border" />

                  {showAdvSearch ? (
                    <p className="py-2 pl-3 text-lg font-medium">Advanced Search</p>
                  ) : (
                    <>
                      <label htmlFor="pcg-cert-search" className="sr-only">
                        Cert Number, Card Name or Set Name
                      </label>
                      <input
                        id="pcg-cert-search"
                        ref={inputRef}
                        type="text"
                        autoComplete="off"
                        className="min-w-0 flex-1 bg-transparent py-2.5 pl-2 pr-1 text-pcg-text placeholder:text-pcg-textDim focus:outline-none"
                        value={value}
                        onChange={(event) => handleOnChange(event.target.value)}
                        placeholder="Cert Number, Card Name or Set Name"
                        onFocus={() => handleDebounce(value)}
                      />
                      {value ? (
                        <button
                          type="button"
                          aria-label="Clear search"
                          onClick={handleClear}
                          className={ICON_BUTTON}
                        >
                          <X aria-hidden="true" className="h-5 w-5" />
                        </button>
                      ) : (
                        <span aria-hidden="true" className={`${ICON_BUTTON} pointer-events-none`}>
                          <Search className="h-5 w-5" />
                        </span>
                      )}
                    </>
                  )}

                  {suggestionsPanel}
                </div>

                {/*
                  PANEL 3 — advanced search. Themed (D22).

                  Collapse's replacement: 0fr -> 1fr on a one-row grid animates a height the
                  CSS never has to be told. What takes the closed fields out of the tab order,
                  the way MUI's unmounting did, is the `inert` set in the effect above — not
                  `invisible`, and never `aria-hidden`.
                */}
                <div
                  className={`grid transition-all duration-200 ease-out ${
                    showAdvSearch
                      ? 'visible grid-rows-[1fr] opacity-100'
                      : 'invisible grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div
                      id="pcg-advanced-search"
                      ref={advancedRef}
                      className={`mx-auto max-w-[900px] p-2 ${PANEL} ${
                        showResults ? '' : 'rounded-b-md'
                      }`}
                    >
                      {advancedMounted ? <SearchCardsAdvanced onSearch={handleAdvancedSearch} /> : null}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/*
              PANEL 4 — the results. Themed (D22), and an overlay: it hangs over the page
              below rather than pushing the whole site down as the visitor types.
            */}
            {/*
              Centred under the box on the full-width band. In the home row the box is a
              column at the left of the page, so the overlay hangs from its left edge
              instead: centred on a narrow column, 900px would run off the left of the screen.
              It also stands a little clear of the fields there: on the inner pages it joins
              the box's squared-off bottom edge, but home's fields are separate rounded
              controls, and a table butting straight onto one reads as a mistake.
            */}
            {showResults && (
              <div
                className={`absolute top-full z-10 w-[900px] max-w-[calc(100vw-2rem)] ${
                  belowBanner ? 'left-0 mt-2' : 'left-1/2 -translate-x-1/2'
                }`}
              >
                <div className={`overflow-hidden rounded-md ${PANEL}`}>
                  {/* error goes to the table, which owns the error state (§12.20). */}
                  <SearchCardsRowTable
                    data={data}
                    isFetching={isFetching}
                    error={error}
                    onClear={closeResults}
                    page={page}
                    rowsPerPage={ROWS_PER_PAGE}
                    onChangePage={(next: number) => setPage(next)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SearchCardsRow;
